'use client';

import React from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {useAuth} from '../../../../hooks/useAuth';
import {useHasRole, useHasAnyRole} from '../../../../hooks/useRoles';
import {type AuthGuardProps} from '../../../../types/auth';
import {AuthLoading} from './AuthLoading';
import {Button} from '@/matic/components/ui/button';
import {Spinner} from '@/matic/components/ui/spinner';
import {stashReturnTo} from '@/matic/lib/auth/post-login-redirect';

/**
 * Insufficient permissions component
 */
const InsufficientPermissions: React.FC<{
  requiredRole?: string;
  requiredRoles?: string[];
  userRole?: string;
}> = ({requiredRole, requiredRoles, userRole}) => (
  <div
    className="flex min-h-[400px] items-center justify-center"
    data-testid="auth-error"
    role="alert"
  >
    <div className="mx-auto max-w-md space-y-4 p-6 text-center">
      <div className="text-lg font-medium text-red-600">
        Insufficient Permissions
      </div>
      <p className="text-gray-600">
        You don't have the required permissions to access this content.
      </p>
      {requiredRole && (
        <div className="space-y-1 text-sm text-gray-500">
          <p>
            Required role: <span className="font-medium">{requiredRole}</span>
          </p>
          {userRole && (
            <p>
              Your role: <span className="font-medium">{userRole}</span>
            </p>
          )}
        </div>
      )}
      {requiredRoles && requiredRoles.length > 0 && (
        <div className="space-y-1 text-sm text-gray-500">
          <p>
            Required roles:{' '}
            <span className="font-medium">{requiredRoles.join(', ')}</span>
          </p>
          {userRole && (
            <p>
              Your role: <span className="font-medium">{userRole}</span>
            </p>
          )}
        </div>
      )}
      <Button
        type="button"
        onClick={() => {
          globalThis.history.back();
        }}
        variant="secondary"
      >
        Go Back
      </Button>
    </div>
  </div>
);

/**
 * Authentication guard component that conditionally renders content based on authentication state
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  fallback,
  requiredRole,
  requiredRoles,
}) => {
  const location = useLocation();

  // Handle authentication hook errors
  try {
    const {isLoading, isAuthenticated, user} = useAuth();

    // Calculate role permissions directly instead of using hooks to avoid circular calls
    const hasRequiredRole = !requiredRole || user?.role === requiredRole;
    const hasAnyRequiredRole =
      !requiredRoles ||
      requiredRoles.length === 0 ||
      (user && requiredRoles.includes(user.role));

    // Show loading state while determining authentication status
    if (isLoading) {
      return <AuthLoading message="Loading authentication status..." />;
    }

    // User is not authenticated
    if (!isAuthenticated) {
      // Use custom fallback if provided
      if (fallback) {
        return <>{fallback}</>;
      }

      // Stash the deep link so IndexRedirect can send the user back here
      // after the Cognito round trip, then redirect to login — LoginPage
      // will trigger the Cognito sign-in flow.
      stashReturnTo(location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }

    // User is authenticated but lacks required role
    if (requiredRole && !hasRequiredRole) {
      return (
        <InsufficientPermissions
          requiredRole={requiredRole}
          userRole={user?.role}
        />
      );
    }

    // User is authenticated but lacks any of the required roles
    if (requiredRoles && requiredRoles.length > 0 && !hasAnyRequiredRole) {
      return (
        <InsufficientPermissions
          requiredRoles={requiredRoles}
          userRole={user?.role}
        />
      );
    }

    // User is authenticated and has required permissions, render children
    return <>{children}</>;
  } catch (error) {
    console.error('AuthGuard error:', error);

    return (
      <div
        className="flex min-h-[400px] items-center justify-center"
        data-testid="auth-error"
        role="alert"
      >
        <div className="space-y-4 text-center">
          <div className="text-lg font-medium text-red-600">
            Authentication Error
          </div>
          <p className="text-gray-600">
            There was a problem loading your authentication status.
          </p>
          <Button
            type="button"
            onClick={() => {
              globalThis.location.reload();
            }}
            variant="primary"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }
};

/**
 * Higher-order component version of AuthGuard for easier composition
 */
export function withAuthGuard<P extends Record<string, unknown>>(
  Component: React.ComponentType<P>,
  options?: Omit<AuthGuardProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <AuthGuard {...options}>
      <Component {...props} />
    </AuthGuard>
  );

  WrappedComponent.displayName = `withAuthGuard(${Component.displayName ?? Component.name})`;

  return WrappedComponent;
}

/**
 * Inline authentication guard for smaller content sections
 */
export const InlineAuthGuard: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requiredRole?: string;
  requiredRoles?: string[];
}> = ({children, fallback, requiredRole, requiredRoles}) => {
  const {isLoading, isAuthenticated} = useAuth();
  const hasRequiredRole = useHasRole(requiredRole ?? '');
  const hasAnyRequiredRole = useHasAnyRole(requiredRoles ?? []);

  if (isLoading) {
    return (
      <div className="inline-flex items-center space-x-2">
        <Spinner size="sm" className="text-primary-600" />
        <span className="text-sm text-gray-600">Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback ? <>{fallback}</> : null;
  }

  // Check role-based access
  if (requiredRole && !hasRequiredRole) {
    return fallback ? <>{fallback}</> : null;
  }

  if (requiredRoles && requiredRoles.length > 0 && !hasAnyRequiredRole) {
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
};

export default AuthGuard;
